import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "fileInfo", "fileName", "fileSize", "defaultValueInfo", "errorInfo", "thumbnail", "removeInput", "changeInput", "signedIdInput", "statusInfo"];
  static values = {
    fileType: String,
    uploadUrl: String,
    maxSize: Number,
    asyncUpload: Boolean
  }

  connect() {
    if (this.hasInputTarget && this.hasFileTypeValue) {
      this.inputTarget.addEventListener("drop", this.preventInvalidDrop.bind(this))
    }
    // Keep original name so Capybara/users can still find the input; strip it only after async upload succeeds.
    this.inputOriginalName = this.hasInputTarget ? this.inputTarget.getAttribute("name") : null

    this.form = this.element.closest("form")
    if (this.form) {
      this.handleSubmit = this.handleSubmit.bind(this)
      this.form.addEventListener("submit", this.handleSubmit)
    }
  }

  disconnect() {
    if (this.form && this.handleSubmit) {
      this.form.removeEventListener("submit", this.handleSubmit)
    }
  }

  parseAcceptedTypes(value) {
    return value
      .split(",")
      .map(type => type.trim().toLowerCase())
      .map(type => type.startsWith(".") ? type : `.${type}`)
  }

  preventInvalidDrop(event) {
    const fileTypes = this.parseAcceptedTypes(this.fileTypeValue)
    const files = event.dataTransfer?.files || []
    const isValid = Array.from(files).every(file => {
      const ext = "." + file.name.split(".").pop().toLowerCase()
      return fileTypes.includes(ext)
    })

    if (!isValid) {
      event.preventDefault()
      event.stopPropagation()
      this.inputTarget.classList.add("input__upload--error")
      this.errorInfoTarget.classList.add("input__message")
      this.errorInfoTarget.classList.remove("hidden")
      this.errorInfoTarget.textContent = "Plan file only accept " + fileTypes.join(", ")
    } else {
      this.inputTarget.classList.remove("input__upload--error")
      this.errorInfoTarget.classList.add("hidden")
    }
  }

  disableUpload() {
    this.element.disabled = true
  }

  showFileInfo(e) {
    const file = e.target.files[0]
    if (!file) return

    // Early guard: enforce client-side size before hitting the async upload endpoint
    if (this.hasMaxSizeValue && file.size > this.maxSizeValue) {
      this.inputTarget.classList.add("input__upload--error")
      this.errorInfoTarget.classList.add("input__message")
      this.errorInfoTarget.classList.remove("hidden")
      this.errorInfoTarget.textContent = `File must be less than ${(this.maxSizeValue / (1024 * 1024)).toFixed(1)} MB`
      this.inputTarget.value = ""
      return
    }

    this.removeInputTarget.value = false
    if (this.hasDefaultValueInfoTarget) {
      this.defaultValueInfoTarget.classList.add("hidden")
    }
    this.inputTarget.classList.add("hidden")
    this.fileInfoTarget.classList.remove("hidden")
    const fileName = file.name
    const fileSize = (file.size / (1024*1024)).toFixed(2)
    this.fileNameTarget.innerHTML = fileName
    this.fileSizeTarget.innerHTML = fileSize + " MB"
    this.inputTarget.classList.remove("input__upload--error")
    if (this.hasErrorInfoTarget) this.errorInfoTarget.classList.add("hidden")
    if (this.hasChangeInputTarget) {
      this.changeInputTarget.value = true
    }
    
    if (this.hasThumbnailTarget) {
      const reader = new FileReader()
      reader.onload = (event) => {
        this.thumbnailTarget.src = event.target.result
        this.thumbnailTarget.classList.remove("hidden")
      }
      reader.readAsDataURL(file)
    }
    this.element.disabled = false

    if (this.asyncUploadValue && this.hasUploadUrlValue) {
      // Upload immediately to get signed_id, so the final form submit stays under gateway size limits
      this.uploadFile(file)
    }
  }

  removeFile() {
    this.inputTarget.value = "";
    this.fileInfoTarget.classList.add("hidden")
    this.inputTarget.classList.remove("hidden")
    if (this.inputOriginalName) {
      this.inputTarget.setAttribute("name", this.inputOriginalName)
    }
    if (this.hasSignedIdInputTarget) {
      this.signedIdInputTarget.value = ""
    }
    if (this.hasStatusInfoTarget) {
      this.statusInfoTarget.classList.add("hidden")
      this.statusInfoTarget.textContent = ""
    }
  }

  removeDefaultValue() {
    this.defaultValueInfoTarget.classList.add("hidden")
    this.removeInputTarget.value = true
    this.changeInputTarget.value = true
    this.inputTarget.classList.remove("hidden")
  }

  async uploadFile(file) {
    if (!this.hasUploadUrlValue) return

    this.setUploading(true)
    this.setStatus("Uploading...")
    try {
      const formData = new FormData()
      formData.append("plan_file", file)

      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content

      const response = await fetch(this.uploadUrlValue, {
        method: "POST",
        headers: csrfToken ? { "X-CSRF-Token": csrfToken } : {},
        body: formData
      })

      const payload = await response.json().catch(() => ({}))

      if (!response.ok) {
        const message = payload.error || "Upload failed"
        this.showError(message)
        this.resetAfterError()
        return
      }

      if (this.hasSignedIdInputTarget && payload.signed_id) {
        this.signedIdInputTarget.value = payload.signed_id
      } else {
        this.showError("Upload missing signed id, please retry.")
        this.resetAfterError()
        return
      }
      this.markUploadSuccess()
    } catch (error) {
      this.showError("Upload failed, please try again")
      this.resetAfterError()
    } finally {
      this.setUploading(false)
    }
  }

  showError(message) {
    if (this.hasErrorInfoTarget) {
      this.errorInfoTarget.classList.add("input__message")
      this.errorInfoTarget.classList.remove("hidden")
      this.errorInfoTarget.textContent = message
    }
    this.inputTarget.classList.add("input__upload--error")
  }

  resetAfterError() {
    this.inputTarget.value = ""
    this.fileInfoTarget.classList.add("hidden")
    this.inputTarget.classList.remove("hidden")
    if (this.hasSignedIdInputTarget) {
      this.signedIdInputTarget.value = ""
    }
    this.clearStatus()
    this.setUploading(false)
  }

  setStatus(message) {
    if (this.hasStatusInfoTarget) {
      this.statusInfoTarget.textContent = message
      this.statusInfoTarget.classList.remove("hidden")
    }
  }

  clearStatus() {
    if (this.hasStatusInfoTarget) {
      this.statusInfoTarget.classList.add("hidden")
      this.statusInfoTarget.textContent = ""
    }
    this.inputTarget.classList.remove("input__upload--error")
  }

  setUploading(isUploading) {
    const form = this.element.closest("form")
    if (!form) return

    const submitButtons = form.querySelectorAll("button[type='submit'], input[type='submit']")
    submitButtons.forEach(btn => { btn.disabled = isUploading })
  }

  markUploadSuccess() {
    if (this.hasStatusInfoTarget) {
      this.statusInfoTarget.textContent = "Uploaded"
      this.statusInfoTarget.classList.remove("hidden")
    }
    this.inputTarget.classList.remove("input__upload--error")
    this.inputTarget.value = ""
    // Strip name to avoid submitting the file payload; Capybara can still target by id.
    if (this.inputOriginalName) {
      this.inputTarget.removeAttribute("name")
    }
  }

  handleSubmit(event) {
    if (!this.hasChangeInputTarget) return

    const changeRequested = this.changeInputTarget.value === "true"
    const missingSignedId = !this.hasSignedIdInputTarget || !this.signedIdInputTarget.value?.trim()

    if (changeRequested && missingSignedId) {
      event.preventDefault()
      event.stopImmediatePropagation?.()
      this.showError("Please re-upload the plan file before saving.")
      this.setStatus("Upload missing. Please re-upload.")
      this.fileInfoTarget.classList.add("hidden")
      this.inputTarget.classList.remove("hidden")
      if (this.inputOriginalName) {
        this.inputTarget.setAttribute("name", this.inputOriginalName)
      }
    }
  }
}
